import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { snapPageFromDirection } from "@/hooks/useDragScrollSnap";
import { AppStatusChoice } from "@/types/borrower";
import { WorkflowBucketGroup } from "./workflowBuckets";
import WorkflowColumn from "./WorkflowColumn";
import { cn } from "@/lib/utils";

interface WorkflowCarouselProps {
  buckets: WorkflowBucketGroup[];
  isLoading: boolean;
  /** Prefix for data-testid hooks (e.g. "workflow-carousel-active"). */
  testId: string;
  /** Tailwind height utility for the carousel viewport. */
  heightClassName?: string;
  /** Number of columns visible at once before the carousel scrolls. */
  visibleColumns?: number;
  /** Column index shown first on mount (default 0). */
  initialStartIndex?: number;
  statusChoices?: AppStatusChoice[];
}

const COLUMN_GAP = 16;

const WorkflowCarousel: React.FC<WorkflowCarouselProps> = ({
  buckets,
  isLoading,
  testId,
  heightClassName = "h-[calc(100vh-22rem-var(--env-ribbon-height))]",
  visibleColumns = 3,
  initialStartIndex = 0,
  statusChoices,
}) => {
  const [startIndex, setStartIndex] = useState(initialStartIndex);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    originIndex: number;
    moved: boolean;
    lastX: number;
    /** Most recent non-trivial pointer step (+ right / − left). */
    lastDelta: number;
  } | null>(null);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const update = () => setViewportWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // When buckets shrink (e.g. search hides empty sections), keep start in range.
  useEffect(() => {
    const maxStart = Math.max(0, buckets.length - visibleColumns);
    setStartIndex((i) => Math.min(i, maxStart));
  }, [buckets.length, visibleColumns]);

  const maxStart = Math.max(0, buckets.length - visibleColumns);
  const clampedStart = Math.min(startIndex, maxStart);
  const canScrollPrev = clampedStart > 0 || dragOffset > 0;
  const canScrollNext = clampedStart < maxStart || dragOffset < 0;

  const columnWidth = useMemo(
    () =>
      viewportWidth > 0 ? (viewportWidth - COLUMN_GAP * (visibleColumns - 1)) / visibleColumns : 0,
    [viewportWidth, visibleColumns]
  );
  const stride = columnWidth + COLUMN_GAP;
  const offset = clampedStart * stride - dragOffset;

  const settleFromDrag = useCallback(
    (deltaX: number, pointerDirection: number, originIndex: number) => {
      if (stride <= 0) {
        setDragOffset(0);
        setIsDragging(false);
        return;
      }
      // Negative deltaX = dragged left = reveal columns to the right = higher index.
      const rawTarget = originIndex - deltaX / stride;
      const target = snapPageFromDirection(rawTarget, pointerDirection, maxStart);
      setStartIndex(target);
      setDragOffset(0);
      setIsDragging(false);
    },
    [maxStart, stride]
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 || columnWidth <= 0) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest("button, a, input, textarea, select, [role='button']")) return;
    // Drag pans columns only — clear any highlight so it cannot block the next drag.
    window.getSelection()?.removeAllRanges();
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      originIndex: clampedStart,
      moved: false,
      lastX: e.clientX,
      lastDelta: 0,
    };
    viewportRef.current?.setPointerCapture(e.pointerId);
  };

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const onMove = (e: PointerEvent) => {
      const state = dragRef.current;
      if (!state || e.pointerId !== state.pointerId) return;
      const dx = e.clientX - state.startX;
      if (!state.moved && Math.abs(dx) < 4) return;
      state.moved = true;
      setIsDragging(true);
      const step = e.clientX - state.lastX;
      if (Math.abs(step) >= 1) state.lastDelta = step;
      state.lastX = e.clientX;
      window.getSelection()?.removeAllRanges();
      setDragOffset(dx);
      e.preventDefault();
    };

    const onUp = (e: PointerEvent) => {
      const state = dragRef.current;
      if (!state || e.pointerId !== state.pointerId) return;
      const dx = e.clientX - state.startX;
      dragRef.current = null;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (state.moved) {
        window.getSelection()?.removeAllRanges();
        const direction = Math.abs(state.lastDelta) >= 1 ? state.lastDelta : dx;
        settleFromDrag(dx, direction, state.originIndex);
      } else {
        setDragOffset(0);
        setIsDragging(false);
      }
    };

    el.addEventListener("pointermove", onMove, { passive: false });
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
  }, [settleFromDrag]);

  return (
    <div className="relative" data-testid={testId} data-start-index={clampedStart}>
      <div
        ref={viewportRef}
        className={cn(
          heightClassName,
          // Always select-none: selection during drag sticks and blocks the next drag.
          "select-none overflow-hidden",
          isDragging ? "cursor-grabbing" : "cursor-grab"
        )}
        onPointerDown={onPointerDown}
        onDragStart={(e) => e.preventDefault()}
      >
        <div
          className={cn(
            "flex h-full items-stretch",
            isDragging ? "transition-none" : "transition-transform duration-300 ease-out"
          )}
          style={{ gap: COLUMN_GAP, transform: `translateX(-${offset}px)` }}
        >
          {buckets.map((bucket) => (
            <div
              key={bucket.key}
              className="h-full shrink-0"
              style={{ width: columnWidth > 0 ? columnWidth : `${100 / visibleColumns}%` }}
            >
              <WorkflowColumn bucket={bucket} isLoading={isLoading} statusChoices={statusChoices} />
            </div>
          ))}
        </div>
      </div>

      {canScrollPrev && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          data-testid={`${testId}-prev`}
          aria-label="Previous sections"
          className="absolute left-2 top-1/2 z-10 h-[54px] w-[54px] -translate-y-1/2 rounded-full bg-background/70 opacity-60 shadow-md backdrop-blur transition hover:bg-background hover:opacity-100"
          onClick={() => setStartIndex(() => Math.max(0, clampedStart - 1))}
        >
          <ChevronLeft className="h-6 w-6" />
        </Button>
      )}

      {canScrollNext && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          data-testid={`${testId}-next`}
          aria-label="Next sections"
          className="absolute right-2 top-1/2 z-10 h-[54px] w-[54px] -translate-y-1/2 rounded-full bg-background/70 opacity-60 shadow-md backdrop-blur transition hover:bg-background hover:opacity-100"
          onClick={() => setStartIndex(() => Math.min(maxStart, clampedStart + 1))}
        >
          <ChevronRight className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
};

export default WorkflowCarousel;
